/**
 * Groups a flat array of survey questions by their category.
 *
 * @param {Array<{ id: string, question: string, category: { id: string, name: string } | null | undefined }>} questions
 * @returns {Record<string, { name: string, questions: Array<{ id: string, question: string }> }>}
 */
export function groupByCategory(questions) {
    const grouped = {}

    for (const question of questions) {
        // Skip questions with null/undefined category or null/undefined category.id
        if (question.category == null || question.category.id == null) {
            continue
        }

        const categoryId = question.category.id
        const categoryName = question.category.name

        if (!(categoryId in grouped)) {
            grouped[categoryId] = { name: categoryName, questions: [] }
        }

        grouped[categoryId].questions.push({
            id: question.id,
            question: question.question,
        })
    }

    return grouped
}

/**
 * Derives the array of selected question IDs from the current active categories
 * and selected questions map.
 *
 * @param {Set<string>} activeCategories - Set of category IDs whose toggles are ON
 * @param {Record<string, string>} selectedQuestions - Map from categoryId to selected questionId
 * @returns {string[]} Array of selected question IDs for all active categories that have a selection
 */
export function deriveValue(activeCategories, selectedQuestions) {
    const result = []

    for (const id of activeCategories) {
        const selection = selectedQuestions[id]
        if (selection != null) {
            result.push(selection)
        }
    }

    return result
}

/**
 * Normalizes question-id selections for comparison (order-independent).
 * @param {unknown} value
 * @returns {string[]}
 */
export function normalizeQuestionIds(value) {
    if (!Array.isArray(value)) return []
    return value.filter((id) => id != null).map(String)
}

/**
 * True when both values represent the same set of question ids (order ignored).
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function areQuestionSelectionsEqual(a, b) {
    const na = normalizeQuestionIds(a).sort()
    const nb = normalizeQuestionIds(b).sort()
    if (na.length !== nb.length) return false
    for (let i = 0; i < na.length; i++) {
        if (na[i] !== nb[i]) return false
    }
    return true
}
